import { defineStore } from 'pinia';

import {
  AccountInitPasswordResponse,
  AccountRegisterResponse,
} from '@/repositories/generated';

export type AccountInitPasswordState = {
  /**
   * パスワード初期化に関する状態
   *
   * REGISTERED: アカウント登録画面からアカウントを登録して、初期パスワードが発行された状態
   * INITIALIZED: アカウント詳細画面からパスワード初期化ボタンを押して、パスワードが初期化された状態
   */
  context: 'INITIALIZED' | 'REGISTERED';
  /** アカウントのid */
  id: string | undefined;
  /**
   * メールアドレス
   */
  email: string | undefined;
  // メールアドレス必須化対応により不要
  // TODO: 運用上本当に消して問題ないなら消す
  initialPassword: string | undefined;
};

/**
 * パスワード初期化情報はできるだけ情報の寿命を短く保持したいです。
 * このため、状態を1度取り出したら状態を破棄できるように、AccountInitPasswordStateを保存するスタックを定義します。
 *
 * pushでスタックに状態を保存します。
 * popでスタックから状態を取り出して、スタックの状態を破棄します。
 */
class AccountInitPasswordStack {
  private state: AccountInitPasswordState | undefined;

  constructor() {
    this.state = undefined;
  }

  push(state: AccountInitPasswordState) {
    this.state = state;
  }

  pop(): AccountInitPasswordState | undefined {
    const state = this.state != null ? { ...this.state } : undefined;
    this.state = undefined;
    return state;
  }

  exists(): boolean {
    return this.state != null;
  }
}

type AccountInitPasswordStoreState = {
  stack: AccountInitPasswordStack;
};

export const useAccountInitPasswordStore = defineStore('accountInitPassword', {
  state: (): AccountInitPasswordStoreState => ({
    stack: new AccountInitPasswordStack(),
  }),
  actions: {
    pushWithAccountRegisterResponse(response: AccountRegisterResponse) {
      const state: AccountInitPasswordState = {
        context: 'REGISTERED',
        id: response.id,
        email: response.email,
        initialPassword: undefined,
      };
      this.stack.push(state);
    },
    pushWithAccountInitPasswordResponse(response: AccountInitPasswordResponse) {
      const state: AccountInitPasswordState = {
        context: 'INITIALIZED',
        id: response.id,
        email: response.email,
        initialPassword: undefined,
      };
      this.stack.push(state);
    },
    popState(): AccountInitPasswordState | undefined {
      return this.stack.pop();
    },
    hasState(): boolean {
      return this.stack.exists();
    },
  },
});
